<script setup lang="ts">
import { MISOProgram } from '@/composables/eos/program-earnings-dashboard'
import type { ProgramEarningsYearly } from '@/types/eos/ProgramEarningsDashboard'
import { formatCurrency } from '@/util/formatters'
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
  type ChartData,
  type ChartOptions,
} from 'chart.js'
import { computed } from 'vue'
import { Bar } from 'vue-chartjs'

const emit = defineEmits<{ select: [{ year: number; month: number; program: MISOProgram }] }>()

/**
 * Stacked vertical bar chart showing total earnings per energy program across
 * the twelve months of a year.
 *
 * When `previousYearData` is provided, each month renders two side-by-side
 * grouped columns (previous year left, current year right) for year-over-year
 * comparison. Without it the chart renders exactly as before.
 *
 * @prop data             - Current-year per-program monthly earnings entries.
 * @prop previousYearData - Optional previous-year entries; enables comparison mode.
 */
const props = defineProps<{
  data?: ProgramEarningsYearly
  previousYearData?: ProgramEarningsYearly
  programFilter?: MISOProgram
  /** Month index (0 = Jan) selected in the parent view; enables highlighting. */
  selectedMonth?: number
  /** Selected program; `MISOProgram.All` (or undefined) highlights the whole month. */
  selectedProgram?: MISOProgram
  /** Selected year — disambiguates the matching column in comparison mode. */
  selectedYear?: number
}>()

// Only the modules actually used are registered — tree-shaking keeps the bundle lean.
ChartJS.register(BarElement, CategoryScale, LinearScale, Title, Tooltip, Legend)

/** Canonical display order — controls the stacking order in the chart. */
const PROGRAMS: MISOProgram[] = [MISOProgram.DAPR, MISOProgram.OR]

const TOOLTIP_LABELS: Record<MISOProgram, string> = {
  [MISOProgram.DAPR]: 'DA PR',
  [MISOProgram.OR]: 'OR',
  [MISOProgram.All]: '',
}

const LEGEND_LABELS: Record<MISOProgram, string> = {
  [MISOProgram.DAPR]: 'Day-Ahead Price Response',
  [MISOProgram.OR]: 'Operating Reserves',
  [MISOProgram.All]: '',
}

/** Fixed brand colours per program. */
const PROGRAM_COLORS: Record<MISOProgram, string> = {
  [MISOProgram.DAPR]: '#0bcd92',
  [MISOProgram.OR]: '#9fccfa',
  [MISOProgram.All]: '',
}

/** Same hues at reduced opacity used for the previous-year columns. */
const PREVIOUS_YEAR_COLORS: Record<MISOProgram, string> = {
  [MISOProgram.DAPR]: 'rgba(11, 205, 146, 0.45)',
  [MISOProgram.OR]: 'rgba(159, 204, 250, 0.45)',
  [MISOProgram.All]: '',
}

/** Short month names that align with JS Date.getMonth() indices (0 = Jan). */
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function buildMatrix(data: ProgramEarningsYearly | undefined): Record<MISOProgram, number[]> {
  const matrix: Record<MISOProgram, number[]> = {
    [MISOProgram.DAPR]: Array(12).fill(0),
    [MISOProgram.OR]: Array(12).fill(0),
    [MISOProgram.All]: Array(12).fill(0),
  }
  if (!data) return matrix
  for (const entry of data) {
    const monthIndex = parseInt(entry.month.split('-')[1], 10) - 1
    if (monthIndex >= 0 && monthIndex < 12 && PROGRAMS.includes(entry.program)) {
      matrix[entry.program][monthIndex] += entry.total_earnings_usd
    }
  }
  return matrix
}

const earningsByProgramAndMonth = computed(() => buildMatrix(props.data))
const previousYearEarningsByProgramAndMonth = computed(() => buildMatrix(props.previousYearData))

const BAR_RADIUS = 3

/**
 * Scriptable `borderRadius` that rounds only the outer corners of a stack:
 * the top corners of the topmost visible segment and the bottom corners of the
 * bottommost one, with `0` everywhere bars touch. Zero-valued (absent) segments
 * are ignored, so the rounding always tracks the segments actually drawn. When a
 * single segment is present it is both top and bottom, so all four corners round.
 *
 * @param program  - The program this dataset renders (its position in the stack).
 * @param matrix   - Per-program monthly values used to find the visible segments.
 * @param programs - Programs in stacking order (index 0 = bottom of the stack).
 */
function stackBorderRadius(program: MISOProgram, matrix: Record<MISOProgram, number[]>, programs: MISOProgram[]) {
  return (ctx: { dataIndex: number }) => {
    const monthIndex = ctx.dataIndex
    if (matrix[program][monthIndex] <= 0) return 0
    const present = programs.filter((p) => matrix[p][monthIndex] > 0)
    const isBottom = present[0] === program
    const isTop = present[present.length - 1] === program
    return {
      topLeft: isTop ? BAR_RADIUS : 0,
      topRight: isTop ? BAR_RADIUS : 0,
      bottomLeft: isBottom ? BAR_RADIUS : 0,
      bottomRight: isBottom ? BAR_RADIUS : 0,
    }
  }
}

/** Alpha applied to bars that don't match the active selection. */
const DIMMED_ALPHA = 0.3

/** Re-expresses a hex / rgb / rgba colour at the given alpha. */
function withAlpha(color: string, alpha: number): string {
  if (color.startsWith('#')) {
    const hex = color.slice(1)
    const r = parseInt(hex.slice(0, 2), 16)
    const g = parseInt(hex.slice(2, 4), 16)
    const b = parseInt(hex.slice(4, 6), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }
  if (color.startsWith('rgba')) return color.replace(/[\d.]+\s*\)$/, `${alpha})`)
  if (color.startsWith('rgb(')) return color.replace('rgb(', 'rgba(').replace(')', `, ${alpha})`)
  return color
}

/**
 * Per-month background colours for one dataset. When a selection is active
 * (`selectedMonth` defined) every bar that doesn't match is dimmed, leaving the
 * selected month/program/year column at full colour. A bar matches when its
 * month equals `selectedMonth`, its program matches `selectedProgram` (or that
 * filter is absent / `All`), and — in comparison mode — its year matches
 * `selectedYear`. With no selection the base colour is used everywhere.
 *
 * @param program     - The program this dataset renders.
 * @param baseColor   - The dataset's full-strength colour.
 * @param datasetYear - The year this dataset represents, or undefined when not comparing.
 */
function barColors(program: MISOProgram, baseColor: string, datasetYear?: string): string[] {
  return MONTH_LABELS.map((_, monthIndex) => {
    if (props.selectedMonth === undefined) return baseColor
    const monthMatches = monthIndex === props.selectedMonth
    const programMatches =
      !props.selectedProgram || props.selectedProgram === MISOProgram.All || props.selectedProgram === program
    const yearMatches =
      datasetYear === undefined || props.selectedYear === undefined || Number(datasetYear) === props.selectedYear
    return monthMatches && programMatches && yearMatches ? baseColor : withAlpha(baseColor, DIMMED_ALPHA)
  })
}

const currentYear = computed(() => props.data?.[0]?.month.split('-')[0] ?? '')
const previousYear = computed(() => props.previousYearData?.[0]?.month.split('-')[0] ?? '')
const isComparison = computed(() => !!props.previousYearData)

/** Programs to render — restricted to the filtered one when a filter is active. */
const activePrograms = computed(() =>
  props.programFilter ? PROGRAMS.filter((p) => p === props.programFilter) : PROGRAMS
)

const chartData = computed<ChartData<'bar', number[], string>>(() => {
  if (!isComparison.value) {
    return {
      labels: MONTH_LABELS,
      datasets: activePrograms.value.map((program) => ({
        datasetId: program,
        label: LEGEND_LABELS[program],
        data: earningsByProgramAndMonth.value[program],
        backgroundColor: barColors(program, PROGRAM_COLORS[program]),
        borderRadius: stackBorderRadius(program, earningsByProgramAndMonth.value, activePrograms.value),
        borderSkipped: false,
      })),
    }
  }

  return {
    labels: MONTH_LABELS,
    datasets: [
      // Previous year — left column in each month group
      ...activePrograms.value.map((program) => ({
        datasetId: `previous-${program}`,
        label: LEGEND_LABELS[program],
        data: previousYearEarningsByProgramAndMonth.value[program],
        backgroundColor: barColors(program, PREVIOUS_YEAR_COLORS[program], previousYear.value),
        borderRadius: stackBorderRadius(program, previousYearEarningsByProgramAndMonth.value, activePrograms.value),
        borderSkipped: false,
        stack: 'previous',
      })),
      // Current year — right column in each month group
      ...activePrograms.value.map((program) => ({
        datasetId: `current-${program}`,
        label: LEGEND_LABELS[program],
        data: earningsByProgramAndMonth.value[program],
        backgroundColor: barColors(program, PROGRAM_COLORS[program], currentYear.value),
        borderRadius: stackBorderRadius(program, earningsByProgramAndMonth.value, activePrograms.value),
        borderSkipped: false,
        stack: 'current',
      })),
    ],
  }
})

const chartOptions = computed<ChartOptions<'bar'>>(() => ({
  responsive: true,
  maintainAspectRatio: false,
  onClick: (_event, elements) => {
    if (!elements.length) return
    const { datasetIndex, index } = elements[0]
    const year =
      isComparison.value && datasetIndex < activePrograms.value.length ? previousYear.value : currentYear.value
    emit('select', {
      year: Number(year),
      month: index,
      program: activePrograms.value[datasetIndex % activePrograms.value.length],
    })
  },
  scales: {
    x: {
      stacked: true,
      grid: { display: false },
      border: { display: false },
    },
    y: {
      stacked: true,
      grid: { color: 'rgba(128,128,128,0.15)' },
      border: { display: false },
      ticks: {
        callback: (value) => (typeof value === 'number' ? formatCurrency(value, 0) : value),
      },
    },
  },
  plugins: {
    legend: {
      position: 'bottom',
      labels: { usePointStyle: true, padding: 16 },
    },
    tooltip: {
      callbacks: {
        title: ([ctx]) =>
          isComparison.value
            ? `${ctx.label} ${ctx.datasetIndex < activePrograms.value.length ? previousYear.value : currentYear.value}`
            : ctx.label,
        label: (ctx) => {
          const program = activePrograms.value[ctx.datasetIndex % activePrograms.value.length]
          const programLabel = TOOLTIP_LABELS[program]
          if (!isComparison.value) return ` ${programLabel}: ${formatCurrency(ctx.parsed.y)}`
          return `${programLabel}: ${formatCurrency(ctx.parsed.y)}`
        },
      },
    },
  },
}))
</script>

<template>
  <Bar :data="chartData" :options="chartOptions" dataset-id-key="datasetId" />
</template>
